import { RestHandler, MockedRequest, DefaultRequestBody, rest } from 'msw';
import { Workpaper } from '../../types/workpaper';
import { formatDate, parseDate, parseStringDate } from '../../utilities/formatting';
import { DB } from '../db';
import { paginate } from '../utils';

const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /workpapers
     * Gets all workpapers for a firm
     */
    rest.get('/workpapers', (req, res, ctx) => {
        const page = parseInt(req.url.searchParams.get('page') || '1');
        const limit = parseInt(req.url.searchParams.get('limit') || '10');
        const search = req.url.searchParams.get('search');
        const sort = req.url.searchParams.get('sort');
        const filter = req.url.searchParams.get('filter');

        let workpapers = DB.GetWorkpapers();
        let totalRows = workpapers.length;

        if (search) {
            workpapers = workpapers.filter(
                (w) =>
                    w.name.toLowerCase().includes(search.toLowerCase()) ||
                    formatDate(new Date(w.periodEnd)).toLowerCase().includes(search.toLowerCase()) ||
                    w.entity.name.toLowerCase().includes(search.toLowerCase())
            );
            totalRows = workpapers.length;
        } else if (filter) {
            const filterObj = JSON.parse(decodeURIComponent(filter));

            if (filterObj.periodEnd) {
                const { base, condition, to } = filterObj.periodEnd;
                if (condition === '>') {
                    workpapers = workpapers.filter((w) => new Date(parseStringDate(w.periodEnd)) > new Date(base));
                } else if (condition === '=') {
                    workpapers = workpapers.filter((w) => parseStringDate(w.periodEnd) === base);
                } else if (condition === '<') {
                    workpapers = workpapers.filter((w) => new Date(parseStringDate(w.periodEnd)) < new Date(base));
                } else {
                    workpapers = workpapers.filter(
                        (w) =>
                            new Date(parseStringDate(w.periodEnd)) >= new Date(base) &&
                            new Date(parseStringDate(w.periodEnd)) <= new Date(to)
                    );
                }
            }

            if (filterObj.entity) {
                const value = filterObj.entity;
                workpapers = workpapers.filter((w) => value.includes(w.entity.id));
            }

            if (filterObj.status) {
                const value = filterObj.status;
                workpapers = workpapers.filter((w) => value.includes(w.status));
            }

            if (filterObj.updatedAt) {
                const { base, condition, to } = filterObj.updatedAt;
                if (condition === '>') {
                    workpapers = workpapers.filter((w) => new Date(parseDate(w.updatedAt)) > new Date(base));
                } else if (condition === '=') {
                    workpapers = workpapers.filter((w) => parseDate(w.updatedAt) === base);
                } else if (condition === '<') {
                    workpapers = workpapers.filter((w) => new Date(parseDate(w.updatedAt)) < new Date(base));
                } else {
                    workpapers = workpapers.filter(
                        (w) => new Date(parseDate(w.updatedAt)) >= new Date(base) && new Date(parseDate(w.updatedAt)) <= new Date(to)
                    );
                }
            }

            if (filterObj.assigned) {
                const value = filterObj.assigned;
                workpapers = workpapers.filter((w) => w.assigned.some((assignee) => value.includes(assignee.id)));
            }

            if (filterObj.status) {
                const value = filterObj.status;
                workpapers = workpapers.filter((w) => w.status === value);
            }

            if (filterObj.progressPercent) {
                const { base, condition, to } = filterObj.progressPercent;
                if (condition === '>') {
                    workpapers = workpapers.filter((w) => w.progressPercent > base);
                } else if (condition === '=') {
                    workpapers = workpapers.filter((w) => w.progressPercent === base);
                } else if (condition === '<') {
                    workpapers = workpapers.filter((w) => w.progressPercent < base);
                } else {
                    workpapers = workpapers.filter((w) => w.progressPercent >= base && w.progressPercent <= to);
                }
            }
            totalRows = workpapers.length;
        }

        if (sort) {
            const { order, option } = JSON.parse(decodeURIComponent(sort));

            if (option === 'periodEnd') {
                workpapers = workpapers.sort((a, b) => (a.periodEnd > b.periodEnd ? 1 : -1));
            } else if (option === 'name') {
                workpapers = workpapers.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
            } else if (option === 'entityName') {
                workpapers = workpapers.sort((a, b) => (a.entity.name.toLowerCase() > b.entity.name.toLowerCase() ? 1 : -1));
            } else if (option === 'updatedAt') {
                workpapers = workpapers.sort((a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));
            } else if (option === 'progressPercentPercent') {
                workpapers = workpapers.sort((a, b) => (a.progressPercent > b.progressPercent ? 1 : -1));
            }
            if (order === 'desc') {
                workpapers = workpapers.reverse();
            }
        }

        return res(
            ctx.delay(),
            ctx.status(200),
            ctx.json({ workpapers: paginate<Workpaper>(workpapers, page, limit), totalItems: totalRows })
        );
    }),

    /**
     * GET /workpapers/:id
     * Gets a specific workpaper
     */
    rest.get('/workpapers/:id', (req, res, ctx) => {
        const workpaper = DB.GetWorkpaper(req.params.id as string);
        return res(ctx.delay(), ctx.status(200), ctx.json({ workpaper }));
    }),

    /**
     * GET /workpapers/:id/user-workpaper-role
     * Gets user's role for a workpaper
     */
    rest.get('/workpapers/:id/user-workpaper-role', (req, res, ctx) => {
        return res(ctx.delay(), ctx.status(200), ctx.json({ role: 'APPROVER' }));
    })
);

export { routes };
